import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemButton,
    LinearProgress,
    styled,
    useTheme
} from '@mui/material';

interface Driver {
    id: string;
    name: string;
    code: string;
    dailyQuota: {
        current: number;
        total: number;
    };
}

interface DriversListProps {
    drivers: Driver[];
    onDriverSelect?: (driver: Driver) => void;
    maxHeight?: number | string;
}

const QuotaProgressBar = styled(LinearProgress)(({ theme, value = 0 }) => {
    let color = theme.palette.warning.main;

    if (value >= 100) {
        color = theme.palette.success.main;
    } else if (value >= 50) {
        color = theme.palette.info.main;
    }

    return {
        height: 8,
        borderRadius: 4,
        backgroundColor: '#e0e0e0',
        '& .MuiLinearProgress-bar': {
            backgroundColor: color,
        }
    };
});

const DriversList: React.FC<DriversListProps> = ({
    drivers,
    onDriverSelect,
    maxHeight = 400
}) => {
    const [selectedDriver, setSelectedDriver] = useState<string | null>(null);

    const handleDriverClick = (driver: Driver) => {
        setSelectedDriver(driver.id);
        if (onDriverSelect) {
            onDriverSelect(driver);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" component="h2" fontWeight="500" mb={1}>
                    Conductores
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    Selecciona uno de los conductores para revisar su historial de balance
                </Typography>
            </Box>

            <Box
                sx={{
                    overflowY: 'auto',
                    maxHeight: maxHeight,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#a8a8a8',
                        },
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 #f1f1f1',
                }}
            >
                <List sx={{ p: 0 }}>
                    {drivers.map((driver) => {
                        const progressPercent = Math.round(
                            (driver.dailyQuota.current / driver.dailyQuota.total) * 100
                        );

                        const isSelected = selectedDriver === driver.id;

                        return (
                            <ListItem
                                key={driver.id}
                                disablePadding
                                sx={{
                                    mb: 2,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    borderColor: isSelected ? '#4a90e2' : '#e0e0e0',
                                }}
                            >
                                <ListItemButton
                                    onClick={() => handleDriverClick(driver)}
                                    sx={{
                                        borderRadius: 2,
                                        p: 2,
                                        '&:hover': {
                                            backgroundColor: 'rgba(74, 144, 226, 0.04)',
                                        },
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 1
                                        }}>
                                            <Typography
                                                variant="subtitle1"
                                                color={isSelected ? 'primary' : 'inherit'}
                                                fontWeight={isSelected ? 500 : 400}
                                            >
                                                {driver.name} - {driver.code}
                                            </Typography>
                                            <Typography variant="body2">
                                                {driver.dailyQuota.current}/{driver.dailyQuota.total}
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Cuota del día
                                        </Typography>

                                        <QuotaProgressBar
                                            variant="determinate"
                                            value={progressPercent}
                                        />
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Box>
        </Paper>
    );
};

export default DriversList;